import { IS_EUROPE } from './region';

// Pinned at build time by the build:eu / build:ru npm scripts, alongside REACT_APP_REGION, so the
// frontend cannot end up pointed at the other region's backend. Unset means local development,
// which falls through to the dev machine below.
const BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.13:8080';

export const API_BASE_URL = BASE_URL;

// Each region serves its own catalogue out of its own bucket, so the asset host follows REGION the
// same way the backend's BUCKET_NAME does (backend/src/utils/region.py). Reads are public and go
// straight to GCS — the backend is only in the path for uploads, via signed PUT URLs.
export const GCS_BUCKET_URL = IS_EUROPE
  ? 'https://storage.googleapis.com/plotexstorage'
  : 'https://storage.googleapis.com/milkywaystorage';

const url = (path) => `${BASE_URL}${path}`;

export const API_URLS = {
  USER_ME:                         url('/me'),
  USER_AI_PREFERENCE:              url('/me/ai-preference'),
  USER_LANGUAGE:                   url('/me/language'),
  AUTHOR_OFFER_ACCEPT:             url('/author_offer/accept'),
  AUTHOR_AGENCY:                   url('/author_agency'),
  USER_BY_ID:                      (userId) => url(`/user/${userId}`),
  USER_REGISTER:                   url('/user'),
  USER_LOGIN:                      url('/login'),
  CONFIRM_EMAIL:                   url('/confirm-email'),
  EMAIL_REWARD:                    url('/email-reward'),
  REQUEST_PASSWORD_RESET:          url('/request-password-reset'),
  RESET_PASSWORD:                  url('/reset-password'),
  SET_EMAIL:                       url('/set-email'),
  RESEND_CONFIRMATION_EMAIL:       url('/resend-confirmation-email'),
  CHANGE_PASSWORD:                 url('/change-password'),
  LINK_TELEGRAM:                   url('/link-telegram'),
  UNLINK_TELEGRAM:                 url('/unlink-telegram'),
  PAYMENT_CREATE:                  url('/payment/create'),
  PAYMENT_VALIDATE_PROMO:          url('/payment/validate-promo'),
  PAYMENT_HISTORY:                 url('/payment/history'),
  PAYMENT_PACKAGES:                url('/payment/packages'),
  PAYMENT_PENDING_ECOMMERCE:       url('/payment/pending-ecommerce'),
  AD_REWARD_TOKEN:                 url('/ad/reward/token'),
  AD_REWARD_CLAIM:                 url('/ad/reward/claim'),
  PAYMENT_REDEEM_STAR_PROMO:       url('/payment/redeem-star-promo'),
  SUBSCRIPTION:                    url('/subscription'),
  SUBSCRIPTION_PURCHASE:           url('/subscription/purchase'),
  SUBSCRIPTION_CLAIM_DAILY:        url('/subscription/claim-daily'),
  MY_NOVELS_TURNS:                 url('/me/novels-turns'),
  AI_MODELS:                       url('/ai-models'),
  IMAGE_MODELS:                    url('/image-models'),
  BUILDING_NOVELS:                 url('/building_novel'),
  BUILDING_ASSETS:                 (novelId) => url(`/building_novel/${novelId}/assets`),
  BUILDING_ASSETS_UPLOAD_URL:      (novelId) => url(`/building_novel/${novelId}/assets/upload-url`),
  BUILDING_ASSETS_CONFIRM:         (novelId) => url(`/building_novel/${novelId}/assets/confirm`),
  BUILDING_ASSET:                  (novelId, assetId) => url(`/building_novel/${novelId}/assets/${assetId}`),
  BUILDING_ASSET_UPLOAD_URL:       (novelId, assetId) => url(`/building_novel/${novelId}/assets/${assetId}/upload-url`),
  BUILDING_INTERACTIVE_SCENES:     (novelId) => url(`/building_novel/${novelId}/interactive_scene`),
  BUILDING_INTERACTIVE_SCENE:      (novelId, sceneId) => url(`/building_novel/${novelId}/interactive_scene/${sceneId}`),
  BUILDING_STATIC_SCENES:          (novelId) => url(`/building_novel/${novelId}/static_scene`),
  BUILDING_STATIC_SCENE:           (novelId, sceneId) => url(`/building_novel/${novelId}/static_scene/${sceneId}`),
  BUILDING_OPEN_WORLD_SCENES:      (novelId) => url(`/building_novel/${novelId}/open_world_scene`),
  BUILDING_OPEN_WORLD_SCENE:       (novelId, sceneId) => url(`/building_novel/${novelId}/open_world_scene/${sceneId}`),
  PRODUCTION_NOVELS:               url('/production_novel'),
  PRODUCTION_NOVEL_BY_ID:          (novelId) => url(`/production_novel/by_id/${novelId}`),
  PRODUCTION_ASSETS:               (novelId) => url(`/production_novel/${novelId}/assets`),
  PRODUCTION_ASSETS_UPLOAD_URL:    (novelId) => url(`/production_novel/${novelId}/assets/upload-url`),
  PRODUCTION_ASSETS_CONFIRM:       (novelId) => url(`/production_novel/${novelId}/assets/confirm`),
  PRODUCTION_ASSET:                (novelId, assetId) => url(`/production_novel/${novelId}/assets/${assetId}`),
  PRODUCTION_ASSET_UPLOAD_URL:     (novelId, assetId) => url(`/production_novel/${novelId}/assets/${assetId}/upload-url`),
  PRODUCTION_INTERACTIVE_SCENES:   (novelId) => url(`/production_novel/${novelId}/interactive_scene`),
  PRODUCTION_INTERACTIVE_SCENE:    (novelId, sceneId) => url(`/production_novel/${novelId}/interactive_scene/${sceneId}`),
  PRODUCTION_STATIC_SCENES:        (novelId) => url(`/production_novel/${novelId}/static_scene`),
  PRODUCTION_STATIC_SCENE:         (novelId, sceneId) => url(`/production_novel/${novelId}/static_scene/${sceneId}`),
  PRODUCTION_OPEN_WORLD_SCENES:    (novelId) => url(`/production_novel/${novelId}/open_world_scene`),
  PRODUCTION_OPEN_WORLD_SCENE:     (novelId, sceneId) => url(`/production_novel/${novelId}/open_world_scene/${sceneId}`),
  PRODUCTION_NOVEL_LIKE:           (novelId) => url(`/production_novel/${novelId}/like`),
  PRODUCTION_NOVEL_LIKE_STATUS:    (novelId) => url(`/production_novel/${novelId}/like-status`),
  PRODUCTION_NOVEL_COMMENTS:       (novelId) => url(`/production_novel/${novelId}/comments`),
  PRODUCTION_NOVEL_COMMENT_DELETE: (novelId, commentId) => url(`/production_novel/${novelId}/comments/${commentId}`),
  PRODUCTION_NOVEL_COMMENT_LIKE:    (novelId, commentId) => url(`/production_novel/${novelId}/comments/${commentId}/like`),
  PRODUCTION_NOVEL_COMMENT_REPLIES: (novelId, commentId) => url(`/production_novel/${novelId}/comments/${commentId}/replies`),
  TEST_INTERACTION:                url('/test_interaction'),
  TEST_OPEN_WORLD_INTERACTION:     url('/test_open_world_interaction'),
  TEST_STATIC_ENDING:              url('/test_static_ending'),
  GAME_CONTINUE:                   (playLink) => url(`/russian/game/${playLink}/continue`),
  GAME_INTERACT:                   (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}`),
  GAME_INTERACT_STREAM:            (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}?stream=true`),
  GAME_HISTORY:                    (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}/history`),
  GAME_HISTORY_ROLLBACK:           (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}/history/rollback`),
  GAME_NEW:                        (playLink) => url(`/russian/game/${playLink}/new_game`),
  GAME_SAVES:                      (novelId) => url(`/game/${novelId}/saves`),
  GAME_CREATE_SAVE:                (novelId, gameStateId) => url(`/game/${novelId}/state/${gameStateId}/save`),
  GAME_DELETE_SAVE:                (novelId, saveId) => url(`/game/${novelId}/save/${saveId}`),
  GAME_SET_BIOGRAPHY:              (novelId) => url(`/russian/game/${novelId}/set_biography`),
  GAME_OW_IMAGE_GENERATION:        (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}/open_world_image_generation`),
  GAME_SUGGESTIONS:                (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}/suggestions`),
  GAME_OW_INFO:                    (novelId, gameStateId) => url(`/russian/game/${novelId}/state/${gameStateId}/open_world_info`),
  USER_NOVEL_GAME_BIOGRAPHY:       (novelId) => url(`/user_novel_game/${novelId}`),
  USER_NOVEL_GAME_OWN_LORE:        (novelId) => url(`/user_novel_game/${novelId}/own_lore`),
  LIBRARY_NOVELS: (limit, offset, nameSubstring = '') => {
    const params = new URLSearchParams({ limit, offset });
    if (nameSubstring) params.append('name_substring', nameSubstring);
    return url(`/library?${params}`);
  },
  LIBRARY_NOVEL:       (playLink) => url(`/library/${playLink}`),
  ADD_TO_LIBRARY:      (playLink) => url(`/library/${playLink}`),
  REMOVE_FROM_LIBRARY: (playLink) => url(`/library/${playLink}`),
  REPORTS_CREATE:      url('/reports'),
  REPORTS_MY:          url('/reports/my'),
  ADMIN_REPORTS:       (skip = 0, limit = 20, answered = undefined) => { const q = new URLSearchParams({ skip, limit }); if (answered !== undefined) q.set('answered', answered); return url(`/admin/reports?${q}`); },
  ADMIN_REPORT_RESPOND: (reportId) => url(`/admin/reports/${reportId}/response`),
  NEWS:                url('/news'),
  NEWS_POST:           (slug) => url(`/news/${slug}`),
  NEWS_UNREAD_COUNT:   url('/news/unread-count'),
  NEWS_MARK_READ:      url('/news/mark-read'),
  ADMIN_NEWS:          url('/admin/news'),
};
